import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity } from '../../utils/security';
import { Activity, RefreshCw, Clock, Eye, X, Shield, Globe, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminLogs = () => {
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedLog, setSelectedLog] = useState(null);

    useEffect(() => {
        if (!authLoading && !isSuperAdmin()) navigate('/admin');
    }, [authLoading, isSuperAdmin, navigate]);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchWithSecurity(`${API_URL}logs.php`);
            const data = await response.json();
            setLogs(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching logs:', error);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        if (user) fetchLogs();
    }, [user, fetchLogs]);

    if (authLoading || !user) return null;

    return (
        <AdminLayout title={t('adminLogs.title') || 'სისტემური და უსაფრთხოების ლოგები'}>
            <div className="shadcn-card">
                <div className="shadcn-card-header d-flex justify-content-between align-items-center">
                    <div className="shadcn-card-title">
                        <Activity size={20} className="text-indigo" />
                        <span>{t('adminLogs.cardTitle') || 'უსაფრთხოების მოვლენების ჟურნალი'} ({logs.length})</span>
                    </div>
                    <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={fetchLogs}>
                        <RefreshCw size={14} /> {t('adminLogs.refresh') || 'განახლება'}
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-5 text-muted">
                        <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                        <span>{t('adminLogs.loading') || 'ლოგები იტვირთება...'}</span>
                    </div>
                ) : logs.length === 0 ? (
                    <p className="text-center text-muted py-5 mb-0">{t('adminLogs.empty') || 'სისტემური აქტივობის ლოგები ჯერ არ არის ჩაწერილი.'}</p>
                ) : (
                    <div className="shadcn-table-wrapper">
                        <table className="shadcn-table">
                            <thead>
                                <tr>
                                    <th>{t('adminLogs.colTimestamp') || 'თარიღი და დრო'}</th>
                                    <th>{t('adminLogs.colUser') || 'მომხმარებელი'}</th>
                                    <th>{t('adminLogs.colAction') || 'მოქმედების ტიპი'}</th>
                                    <th>{t('adminLogs.colDetails') || 'დეტალები'}</th>
                                    <th>{t('adminLogs.colIp') || 'IP მისამართი'}</th>
                                    <th className="text-end">{t('common.actions') || 'მოქმედება'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(log => (
                                    <tr 
                                        key={log.id} 
                                        className="cursor-pointer hover-bg-slate"
                                        onClick={() => setSelectedLog(log)}
                                    >
                                        <td className="small text-muted">
                                            <div className="d-flex align-items-center gap-1">
                                                <Clock size={13} />
                                                <span>{new Date(log.created_at).toLocaleString()}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="shadcn-badge shadcn-badge-primary">{log.username}</span>
                                        </td>
                                        <td>
                                            <span className="shadcn-badge shadcn-badge-info font-monospace">
                                                {log.action}
                                            </span>
                                        </td>
                                        <td className="small text-dark text-truncate" style={{ maxWidth: '300px' }}>
                                            {log.details}
                                        </td>
                                        <td className="small font-monospace text-muted">{log.ip_address}</td>
                                        <td className="text-end">
                                            <button 
                                                className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary" 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedLog(log);
                                                }}
                                                title={t('common.details') || 'დეტალების ნახვა'}
                                            >
                                                <Eye size={14} /> {t('common.details') || 'ნახვა'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Log Details Popup Modal */}
            {selectedLog && (
                <div 
                    className="modal fade show d-block" 
                    tabIndex="-1" 
                    style={{ backgroundColor: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 1050 }}
                    onClick={() => setSelectedLog(null)}
                >
                    <div 
                        className="modal-dialog modal-dialog-centered modal-lg" 
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content shadow-lg border-0 rounded-4 overflow-hidden">
                            <div className="modal-header bg-slate-900 text-white p-4">
                                <div className="d-flex align-items-center gap-2">
                                    <Shield className="text-indigo" size={22} style={{ color: '#818cf8' }} />
                                    <h5 className="modal-title fw-bold text-white mb-0">
                                        {t('adminLogs.modalTitle') || 'უსაფრთხოების ლოგის დეტალები'} #{selectedLog.id}
                                    </h5>
                                </div>
                                <button 
                                    type="button" 
                                    className="btn-close btn-close-white" 
                                    onClick={() => setSelectedLog(null)} 
                                />
                            </div>

                            <div className="modal-body p-4 bg-white">
                                <div className="row g-3 mb-4">
                                    <div className="col-md-6">
                                        <div className="p-3 rounded-3 bg-light border">
                                            <span className="text-muted smallest d-block mb-1">{t('adminLogs.colUser') || 'მომხმარებლის ანგარიში'}</span>
                                            <div className="d-flex align-items-center gap-2">
                                                <User size={16} className="text-primary" />
                                                <span className="fw-bold text-dark">{selectedLog.username}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="p-3 rounded-3 bg-light border">
                                            <span className="text-muted smallest d-block mb-1">{t('adminLogs.colAction') || 'მოქმედების ტიპი'}</span>
                                            <span className="shadcn-badge shadcn-badge-info font-monospace fs-7">
                                                {selectedLog.action}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="p-3 rounded-3 bg-light border">
                                            <span className="text-muted smallest d-block mb-1">{t('adminLogs.colTimestamp') || 'თარიღი და დრო'}</span>
                                            <div className="d-flex align-items-center gap-1 text-dark small fw-semibold">
                                                <Clock size={14} className="text-muted" />
                                                <span>{new Date(selectedLog.created_at).toLocaleString()}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="p-3 rounded-3 bg-light border">
                                            <span className="text-muted smallest d-block mb-1">{t('adminLogs.colIp') || 'IP მისამართი'}</span>
                                            <div className="d-flex align-items-center gap-1 font-monospace text-dark small fw-semibold">
                                                <Globe size={14} className="text-primary" />
                                                <span>{selectedLog.ip_address}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-group-label mb-2 fw-bold text-dark">{t('adminLogs.detailsPayload') || 'ლოგის სრული მონაცემები & დეტალები:'}</label>
                                    <div className="p-3 rounded-3 bg-slate-900 text-slate-100 font-monospace small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#e2e8f0', background: '#0f172a' }}>
                                        {selectedLog.details}
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer bg-light p-3">
                                <button 
                                    type="button" 
                                    className="shadcn-btn shadcn-btn-secondary" 
                                    onClick={() => setSelectedLog(null)}
                                >
                                    <X size={16} /> {t('common.close') || 'დახურვა'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminLogs;
